import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getPropertiesById } from "../../Services/Properties";
import AgentDetails from "./agent-details";
import FloorPlan from "./floor-plan";
import PropertyDetails from "./property-details";
import CoverPhoto from "./cover-photo";
import HeroPhotos from "./hero-photos";
import PropertySummary from "./property-summary";
import FilterReport from "./filter-report";
import { PropertyReport } from "../../Models/PropertyReport";
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useTranslation } from "react-i18next";
import LocationReport from "./location-report";
import ButtonGenerate from "./button-generate";
import TextAreaForm from "./textarea-form";
import { Dialog, DialogTitle, IconButton, DialogContent, DialogActions } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PagePDF from "./Pdf/page-pdf";
import { GenerateReport } from "../../Services/PdfReport";
import { GetCompanyProfile } from "../../Services/Profile";
import GeneratedPdf from "./Pdf/generated-pdf";
import SectionPhotos from "./section-photos";
import DocumentPdf from "./Pdf/document-pdf";
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';


const CreatePropertyReport: React.FunctionComponent<{}> = (props) => {

    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const idValue = location.pathname.split('/');
    const [reportValue, setReportValue] = useState<PropertyReport>({});
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState<string>();
    const [colorSnackbar, setColorSnackbar] = useState<any>("");
    const { t } = useTranslation();
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [images, setImages] = useState<any[]>([]);
    const [propertyId, setPropertyId] = useState<any>(idValue[2] ?? "");
    const [isOpenPdf, setIsOpenPdf] = useState<boolean>(false);
    const [idPdf, setIdPdf] = useState<any>(null);
    const [isChange, setIsChange] = useState<boolean>(false);
    const [isPdfLoading, setIsPdfLoading] = useState<boolean>(true);
    const [pdfReportData, setPdfReportData] = useState<PropertyReport | null>(null);

    const customOrder = [
        "living_room", "dining_room", "room-bedroom",
        "bathroom", "kitchen", "balcony", "cellar", "details",
        "empty_room", "garden", "gym", "hall-corridor", "map",
        "mountain_view", "non_related", "office", "outdoor_house",
        "parking", "pool", "reception-lobby", "stairs",
        "storage_pantry", "terrace", "walk_in_closet",
        "floorplan", "brochure"
    ];

    function getCoverAndHeroImage(images: any[]): [string?, string?] {
        const filtered = images.filter(img => img.room_type !== "floorplan");

        const sorted = filtered.sort((a, b) => {
            const aIndex = customOrder.indexOf(a.room_type);
            const bIndex = customOrder.indexOf(b.room_type);

            const aRank = aIndex === -1 ? Infinity : aIndex;
            const bRank = bIndex === -1 ? Infinity : bIndex;

            if (aRank === bRank) {
                return b.image_quality_score - a.image_quality_score;
            }

            return aRank - bRank;
        });

        const coverImage = sorted.find(Boolean);
        const coverRoomType = coverImage?.room_type;

        const heroImage = sorted.find(img => img.room_type !== coverRoomType);

        return [coverImage?.src, heroImage?.src];
    }

    function getSortedImageSrcs(images: any[]): string[] {
        return images
            .filter((item) => item.room_type !== "floorplan")
            .sort((a, b) => b.image_quality_score - a.image_quality_score)
            .map((item) => item.src);
    }

    useEffect(() => {
        if (propertyId) {
            Promise.all([
                getPropertiesById(propertyId),
                GetCompanyProfile()
            ]).then(([propertyData, companyProfile]) => {
                setImages(getSortedImageSrcs(propertyData.content.images));
                setReportValue({
                    listingImages: propertyData.content.images,
                    heroSection: {
                        coverImageUrl: getCoverAndHeroImage(propertyData.content.images)[0],
                        heroImageUrl: getCoverAndHeroImage(propertyData.content.images)[1],
                        title: propertyData.content.title[0]?.title,
                        location: propertyData.content.address.subCommunity + ", " + propertyData.content.address.Community + ", " + propertyData.content.address.city,
                        address: {
                            buildingName: propertyData.content.address.buildingName,
                            city: propertyData.content.address.buildingName,
                            address: propertyData.content.address.address,
                            country: propertyData.content.address.country ? propertyData.content.address.country : "UAE",
                            latitude: propertyData.content.address.latitude,
                            longitude: propertyData.content.address.longitude,
                            subCommunity: propertyData.content.address.subCommunity,
                            Community: propertyData.content.address.Community,
                        }
                    },
                    propertyGallery: {
                        offeringType: propertyData.content.offeringType,
                        propertyType: propertyData.content.propertyType,
                        images: getSortedImageSrcs(propertyData.content.images)
                    },
                    propertyDetails: {
                        bedrooms: propertyData.content.bedroom,
                        bathrooms: propertyData.content.bathroom,
                        size: {
                            unit: propertyData.content.size.unit,
                            value: propertyData.content.size.builtUp
                        },
                        price: {
                            amount: propertyData.content.price.amount.toString().replace(/,/g, ''),
                            currency: propertyData.content.price.currency
                        },
                        description: (
                            (() => {
                                const descriptions = Array.isArray(propertyData.content.description) ? propertyData.content.description : [];
                                const englishItem = descriptions.find((item: any) => {
                                    const lang = (item?.lang || "").toString().toLowerCase();
                                    return lang === "english" || lang === "en" || lang === "eng";
                                });
                                const selectedItem = englishItem || descriptions[0];
                                const raw = selectedItem?.desc || "";
                                return raw.replace(/[\r\n\t]/g, ' ').substring(0, 1000);
                            })()
                        ),
                        image: propertyData.content.images.find(
                            (item: any) => (item.room_type === "outdoor_house" || item.room_type === "outdoor_building")
                        )?.src
                    },
                    floorPlan: {
                        description: "Note:This floor plan is for illustrative purposes only. Dimensions, layouts, and fixtures are approximate and may vary slightly from the actual property. Please consult with your agent for precise details.",
                        imageUrl: propertyData.content.images.find(
                            (item: any) => item.room_type === "floorplan"
                        )?.src
                    },
                    agentInfo: {
                        photoUrl: propertyData.content.agent.photo.src ? propertyData.content.agent.photo.src : "https://static.vecteezy.com/system/resources/previews/009/292/244/non_2x/default-avatar-icon-of-social-media-user-vector.jpg",
                        name: propertyData.content.agent.name,
                        phone: propertyData.content.agent.phoneNumbers,
                        email: propertyData.content.agent.emailAddresses,
                        languages: propertyData.content.agent.langs
                    },
                    company: {
                        name: companyProfile.companyName,
                        email: companyProfile.companyEmail,
                        phone: companyProfile.companyPhone,
                        logo: companyProfile.companyLogo,
                        address: companyProfile.companyAddress
                    },
                    location: {
                        description: ""
                    },
                });

                setLoading(false);
            }).catch((error) => {
                ChangeMessageHandler("error", error?.response?.data?.message || "An error occurred");
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [propertyId]);

    const ChangeHeroImageHandler = (src: string) => {
        setReportValue(prev => ({
            ...prev,
            heroSection: {
                ...prev?.heroSection,
                heroImageUrl: src
            }
        }))
    }

    const ChangeCoverHandler = (src: any) => {
        setReportValue(prev => ({
            ...prev,
            heroSection: {
                ...prev?.heroSection,
                coverImageUrl: src
            }
        }))
    }

    const ChangeHeroPhotosHandler = (images: any) => {
        setReportValue(prev => ({
            ...prev,
            propertyGallery: {
                ...prev?.propertyGallery,
                images: images
            }
        }))
    }

    const ChangeImageHandler = (src: any) => {
        setReportValue(prev => ({
            ...prev,
            propertyDetails: {
                ...prev?.propertyDetails,
                image: src
            }
        }))
    }

    const ChangePhotosHandler = (imageslist: any) => {
        setReportValue(prev => ({
            ...prev,
            listImages: imageslist
        }))
    }

    const ChangeFloorPlanHandler = (src: any) => {
        setReportValue(prev => ({
            ...prev,
            floorPlan: {
                ...prev?.floorPlan,
                imageUrl: src
            }
        }))
    }

    const ChangeNotesHandler = (value: any) => {
        setReportValue(prev => ({
            ...prev,
            floorPlan: {
                ...prev?.floorPlan,
                description: value
            }
        }))
    }

    const handleChangeDescLocation = (value: any) => {
        setReportValue(prev => ({
            ...prev,
            location: {
                ...prev?.location,
                description: value
            }
        }))
    }

    const ChangeDescHandler = (value: any) => {
        setReportValue(prev => ({
            ...prev,
            propertyDetails: {
                ...prev?.propertyDetails,
                description: value
            }
        }))
    }

    const ChangePropertyHandler = (id: any) => {
        setPropertyId(id)
    }

    const HandleChangeOffering = (value: any) => {
        setReportValue(prev => ({
            ...prev,
            propertyGallery: {
                ...prev?.propertyGallery,
                offeringType: value
            }
        }))
    }

    const handleChange = (event: any) => {
        const { name, value } = event.target;
        setReportValue(prev => ({
            ...prev,
            propertyDetails: {
                ...prev?.propertyDetails,
                [name]: value
            }
        }))
    }

    const handleChangeSize = (event: any) => {
        const { name, value } = event.target;
        setReportValue(prev => ({
            ...prev,
            propertyDetails: {
                ...prev?.propertyDetails,
                size: {
                    ...prev?.propertyDetails?.size,
                    [name]: value
                }
            }
        }))
    }

    const handleChangeServiceCharge = (event: any) => {
        const { name, value } = event.target;
        setReportValue(prev => ({
            ...prev,
            propertyDetails: {
                ...prev?.propertyDetails,
                serviceCharge: {
                    ...prev?.propertyDetails?.serviceCharge,
                    [name]: value
                }
            }
        }))
    }

    const handleChangePrice = (event: any) => {
        const { name, value } = event.target;
        setReportValue(prev => ({
            ...prev,
            propertyDetails: {
                ...prev?.propertyDetails,
                price: {
                    ...prev?.propertyDetails?.price,
                    [name]: value
                }
            }
        }))
    }

    const ChangeAgentHandler = (agent: any) => {
        setReportValue((prev) => ({
            ...prev,
            agentInfo: {
                photoUrl: agent.image,
                name: agent.fullName,
                phone: agent.phoneNumber,
                email: agent.email,
                languages: agent.languages
            }
        }))
    }

    const ChangeLocationHandler = (address: any) => {
        setReportValue((prev) => ({
            ...prev,
            heroSection: {
                ...prev.heroSection,
                address: {
                    city: address.city,
                    country: address.country,
                    latitude: address.latitude,
                    longitude: address.longitude,
                    subCommunity: address.subCommunity,
                    community: address.community,
                },
                location: address.location,
            }
        }))
    }

    const ChangeColorHandler = (color: any) => {
        setReportValue(prev => ({
            ...prev,
            color: color
        }))
    }

    const handleCloseSnackbar = (
        event?: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason,
    ) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const convertBlobToBase64 = (blob: Blob): Promise<string | ArrayBuffer | null> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const generateMapboxStaticImage = async (lat: number, lng: number, width = 800, height = 500, zoom = 13): Promise<string> => {
        const MAPBOX_ACCESS_TOKEN = "sk.eyJ1Ijoia2FvdXRhcjM0NTYiLCJhIjoiY203aXl1OW1iMDFvNTJrc2N5MjVxaTM5dyJ9.liGIzZ8bLftpvi635AiVYw";
        const MAPBOX_STYLE = "mapbox/streets-v11";
        const url = `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE}/static/pin-l+ff0000(${lng},${lat})/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`
        const response = await fetch(url);
        const blob = await response.blob();
        return await convertBlobToBase64(blob) as string;
    };

    // Convert a single image URL to base64 using multiple methods
    const convertImageToBase64 = (src: string, isCompanyLogo: boolean = false): Promise<string> => {
        return new Promise(async (resolve) => {
            if (!src || src.startsWith('data:')) {
                resolve(src); // Already base64 or empty
                return;
            }

            const token = localStorage.getItem("usertoken");

            // Method 0: Try axios with auth token - ONLY for company logo from internal API
            const tryAxiosMethod = async (): Promise<string | null> => {
                // Only use axios for company logo that's from our internal API
                if (!isCompanyLogo || !src.includes('api-v1.coralytics.com')) {
                    return null;
                }

                try {
                    console.log(`Trying axios for company logo: ${src.substring(0, 60)}...`);
                    const response = await axios.get(src, {
                        responseType: 'blob',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'image/*'
                        }
                    });

                    if (response.data) {
                        return new Promise((res) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                const result = reader.result as string;
                                if (result && result.startsWith('data:')) {
                                    console.log(`✓ Axios method worked for company logo`);
                                    res(result);
                                } else {
                                    res(null);
                                }
                            };
                            reader.onerror = () => res(null);
                            reader.readAsDataURL(response.data);
                        });
                    }
                    return null;
                } catch (error) {
                    console.log(`Axios method failed for company logo, trying other methods...`);
                    return null;
                }
            };

            // Method 1: Canvas method with CORS (primary method for most images)
            const tryCanvasMethod = (useCors: boolean): Promise<string | null> => {
                return new Promise((res) => {
                    const img = new window.Image();

                    if (useCors) {
                        img.crossOrigin = 'anonymous';
                    }

                    img.onload = () => {
                        try {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.naturalWidth || 800;
                            canvas.height = img.naturalHeight || 600;

                            const ctx = canvas.getContext('2d');
                            if (ctx) {
                                ctx.drawImage(img, 0, 0);
                                const base64 = canvas.toDataURL('image/png');
                                console.log(`✓ Canvas method (CORS=${useCors}) worked for: ${src.substring(0, 50)}...`);
                                res(base64);
                            } else {
                                res(null);
                            }
                        } catch (error) {
                            console.log(`Canvas error (CORS=${useCors}): ${(error as any)?.message}`);
                            res(null);
                        }
                    };

                    img.onerror = () => {
                        console.log(`Image load error (CORS=${useCors}): ${src.substring(0, 40)}...`);
                        res(null);
                    };

                    img.src = src;
                });
            };

            // Method 2: Fetch API method
            const tryFetchMethod = async (): Promise<string | null> => {
                try {
                    const response = await fetch(src, {
                        mode: 'cors',
                        credentials: 'omit',
                        headers: {
                            'Accept': 'image/*'
                        }
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        return new Promise((res) => {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                                const result = reader.result as string;
                                if (result && result.startsWith('data:')) {
                                    console.log(`✓ Fetch method worked for: ${src.substring(0, 50)}...`);
                                    res(result);
                                } else {
                                    res(null);
                                }
                            };
                            reader.onerror = () => res(null);
                            reader.readAsDataURL(blob);
                        });
                    }
                    return null;
                } catch (error) {
                    return null;
                }
            };

            // Try methods in order
            let result: string | null = null;

            // For company logo, try axios first
            if (isCompanyLogo) {
                result = await tryAxiosMethod();
                if (result) {
                    resolve(result);
                    return;
                }
            }

            // 1. Try canvas with CORS first (works for most CDN images)
            result = await tryCanvasMethod(true);
            if (result) {
                resolve(result);
                return;
            }

            // 2. Try fetch method
            result = await tryFetchMethod();
            if (result) {
                resolve(result);
                return;
            }

            // 3. Try canvas without CORS
            result = await tryCanvasMethod(false);
            if (result) {
                resolve(result);
                return;
            }

            // 4. All methods failed, return original URL
            console.warn(`✗ All conversion methods failed for: ${src}`);
            resolve(src);
        });
    };

    // Convert all images in the report to base64 for reliable PDF rendering
    const convertAllImagesToBase64 = async (report: PropertyReport): Promise<PropertyReport> => {
        const updatedReport = JSON.parse(JSON.stringify(report)) as PropertyReport;

        // Store the company logo URL to identify it later
        const companyLogoUrl = updatedReport.company?.logo;

        // Create a map of original URLs to base64
        const imageUrls: string[] = [];

        // Collect all unique image URLs
        if (updatedReport.heroSection?.coverImageUrl) imageUrls.push(updatedReport.heroSection.coverImageUrl);
        if (updatedReport.heroSection?.heroImageUrl) imageUrls.push(updatedReport.heroSection.heroImageUrl);
        if (updatedReport.propertyGallery?.images) {
            imageUrls.push(...updatedReport.propertyGallery.images.filter(Boolean));
        }
        if (updatedReport.listImages) {
            updatedReport.listImages.forEach((img: any) => {
                if (img?.src) imageUrls.push(img.src);
            });
        }
        if (updatedReport.propertyDetails?.image) imageUrls.push(updatedReport.propertyDetails.image);
        if (updatedReport.floorPlan?.imageUrl) imageUrls.push(updatedReport.floorPlan.imageUrl);
        if (updatedReport.agentInfo?.photoUrl) imageUrls.push(updatedReport.agentInfo.photoUrl);
        if (updatedReport.company?.logo) imageUrls.push(updatedReport.company.logo);

        // Remove duplicates and filter out already base64 images
        const uniqueUrls = Array.from(new Set(imageUrls)).filter(url => url && !url.startsWith('data:'));

        console.log(`Converting ${uniqueUrls.length} images to base64...`);

        // Convert all images sequentially to avoid race conditions
        const urlToBase64Map = new Map<string, string>();

        for (let i = 0; i < uniqueUrls.length; i++) {
            const url = uniqueUrls[i];
            try {
                // Check if this is the company logo - use special handling
                const isCompanyLogo = url === companyLogoUrl;
                const base64 = await convertImageToBase64(url, isCompanyLogo);
                const isConverted = base64.startsWith('data:');
                urlToBase64Map.set(url, base64);
                console.log(`[${i + 1}/${uniqueUrls.length}] ${isConverted ? '✓' : '✗'} ${isCompanyLogo ? '[LOGO] ' : ''}${url.substring(0, 50)}...`);
            } catch (error) {
                console.error(`Failed to convert: ${url}`, error);
                urlToBase64Map.set(url, url); // Keep original URL as fallback
            }
        }

        // Helper function to replace URL with base64
        const replaceWithBase64 = (url: string | undefined): string | undefined => {
            if (!url) return url;
            if (url.startsWith('data:')) return url; // Already base64
            return urlToBase64Map.get(url) || url;
        };

        // Update all image URLs in the report
        if (updatedReport.heroSection) {
            updatedReport.heroSection.coverImageUrl = replaceWithBase64(updatedReport.heroSection.coverImageUrl);
            updatedReport.heroSection.heroImageUrl = replaceWithBase64(updatedReport.heroSection.heroImageUrl);
        }

        if (updatedReport.propertyGallery?.images) {
            updatedReport.propertyGallery.images = updatedReport.propertyGallery.images.map(
                (img: string) => replaceWithBase64(img) || img
            );
        }

        if (updatedReport.listImages) {
            updatedReport.listImages = updatedReport.listImages.map((img: any) => ({
                ...img,
                src: replaceWithBase64(img?.src)
            }));
        }

        if (updatedReport.propertyDetails) {
            updatedReport.propertyDetails.image = replaceWithBase64(updatedReport.propertyDetails.image);
        }

        if (updatedReport.floorPlan) {
            updatedReport.floorPlan.imageUrl = replaceWithBase64(updatedReport.floorPlan.imageUrl);
        }

        if (updatedReport.agentInfo) {
            updatedReport.agentInfo.photoUrl = replaceWithBase64(updatedReport.agentInfo.photoUrl);
        }

        if (updatedReport.company) {
            updatedReport.company.logo = replaceWithBase64(updatedReport.company.logo);
        }

        // Count how many were successfully converted
        const convertedCount = Array.from(urlToBase64Map.values()).filter(v => v.startsWith('data:')).length;
        console.log(`Conversion complete: ${convertedCount}/${uniqueUrls.length} images converted to base64`);

        return updatedReport;
    };

    const validateForm = () => {
        const newErrors: { [key: string]: boolean } = {};

        if (!reportValue.heroSection?.coverImageUrl) newErrors.coverImageUrl = true;
        if (!reportValue.heroSection?.heroImageUrl) newErrors.heroImageUrl = true;
        if (!reportValue.propertyGallery?.images || reportValue.propertyGallery.images.length < 6) {
            newErrors.images = true;
        } if (!reportValue.propertyDetails?.image) newErrors.propertyDetailsImage = true;
        if (!reportValue.propertyDetails?.description) newErrors.propertyDetailsDesc = true;
        if (!reportValue.propertyDetails?.bedrooms) newErrors.bedrooms = true;
        if (!reportValue.propertyDetails?.bathrooms) newErrors.bathrooms = true;
        if (!reportValue.propertyDetails?.price?.amount) newErrors.price = true;
        if (!reportValue.propertyDetails?.size?.value) newErrors.size = true;
        if (!reportValue.heroSection?.location) newErrors.address = true;
        if (!reportValue.location?.description) newErrors.addressDescription = true;

        setErrors(newErrors);

        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    }

    const getValidationErrorMessage = (validationErrors: { [key: string]: boolean }) => {
        const errorMessages: { [key: string]: string } = {
            coverImageUrl: "Cover Image",
            heroImageUrl: "Hero Image",
            images: "Property Gallery (minimum 6 images required)",
            propertyDetailsImage: "Property Details Image",
            propertyDetailsDesc: "Property Description",
            bedrooms: "Number of Bedrooms",
            bathrooms: "Number of Bathrooms",
            price: "Property Price",
            size: "Property Size",
            address: "Property Address/Location",
            addressDescription: "Location Description",
        };

        const missingFields = Object.keys(validationErrors).map(key => errorMessages[key]).join(", ");

        return `Please review the form. The following required fields are missing or incorrectly filled: ${missingFields}.`;
    }

    const Generate = async () => {
        const validationResult = validateForm();

        if (validationResult.isValid) {
            try {
                // Show loading state
                setIsPdfLoading(true);
                setMessage("Preparing images...");
                setColorSnackbar("info");
                setOpenSnackbar(true);

                const base64Url = await generateMapboxStaticImage(reportValue?.heroSection?.address?.latitude, reportValue?.heroSection?.address?.longitude);

                const updatedReportValue = {
                    ...reportValue,
                    location: {
                        ...reportValue?.location,
                        mapImageUrl: base64Url
                    }
                };

                setMessage("Converting images for PDF...");

                // Convert all images to base64 for reliable PDF rendering
                const reportWithBase64Images = await convertAllImagesToBase64(updatedReportValue);

                // Store the converted report for the PDF viewer
                setPdfReportData(reportWithBase64Images);

                setMessage("Generating PDF...");

                // Debug: Log the converted report data
                const coverConverted = !!reportWithBase64Images.heroSection?.coverImageUrl?.startsWith('data:');
                const heroConverted = !!reportWithBase64Images.heroSection?.heroImageUrl?.startsWith('data:');
                const galleryConverted = reportWithBase64Images.propertyGallery?.images?.filter(img => img?.startsWith('data:')).length || 0;
                const galleryTotal = reportWithBase64Images.propertyGallery?.images?.length || 0;

                console.log('PDF Report Data ready:', {
                    coverImage: coverConverted ? 'base64 ✓' : 'url (may not render)',
                    heroImage: heroConverted ? 'base64 ✓' : 'url (may not render)',
                    galleryImages: `${galleryConverted}/${galleryTotal} converted to base64`,
                });

                // Show warning if images weren't converted
                if (!coverConverted || !heroConverted || galleryConverted < galleryTotal) {
                    console.warn('⚠️ Some images could not be converted to base64. They may not appear in the PDF due to CORS restrictions.');
                    setMessage(`Images prepared. Note: ${galleryConverted}/${galleryTotal} gallery images converted successfully.`);
                }

                return new Promise((resolve, reject) => {
                    GenerateReport(updatedReportValue).then(resp => {
                        setIdPdf(resp.data._id);
                        setOpenSnackbar(false);
                        // Open PDF dialog after data is ready
                        setIsOpenPdf(true);
                        // Hide loader after PDF viewer has had time to render
                        setTimeout(() => setIsPdfLoading(false), 3000);
                        resolve(resp);
                        setIsChange(!isChange);
                    }).catch(error => {
                        reject(error);
                        setColorSnackbar("error");
                        setOpenSnackbar(true);
                        console.log(error);
                        setMessage((error as any).response?.data?.error || "An error occurred");
                    })
                });
            } catch (error) {
                console.error("Error during PDF generation:", error);
                setColorSnackbar("error");
                setOpenSnackbar(true);
                setMessage("Failed to prepare images for PDF generation");
                setIsPdfLoading(false);
            }
        } else {
            const msg = getValidationErrorMessage(validationResult.errors);
            setOpenSnackbar(true);
            setMessage(msg);
            setColorSnackbar("error");
        }
    }

    const ChangeMessageHandler = (color: string, msg: string) => {
        setColorSnackbar(color)
        setOpenSnackbar(true)
        setMessage(msg)
    }

    return loading ? <div className="container-xxl flex-grow-1 container-p-y marg-bottom-100">Loading...
        {message && <Snackbar open={openSnackbar} onClose={handleCloseSnackbar} autoHideDuration={10000}>
            <Alert
                onClose={handleCloseSnackbar}
                severity={colorSnackbar || "info"}
                variant="filled"
                sx={{ width: '100%' }}
            >
                {message}
            </Alert>
        </Snackbar>}
    </div> : (

        <div className="container-xxl flex-grow-1 container-p-y">
            <div className="marg-bottom-50">
                <div className="row">
                    <div className="col-1 d-flex align-items-center">
                        <div className="marg-right-10">
                            <a href={`/properties/${idValue[2]}`} className="buuton-next border-trans marg-right-10 marg-right-16 back-FFFFFF border-9" >
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 14L13 7" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 14L13 7" stroke="black" strokeOpacity="0.2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 14H22.3333" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 14H22.3333" stroke="black" strokeOpacity="0.2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 14L13 21" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 14L13 21" stroke="black" strokeOpacity="0.2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </a>
                        </div>
                        <div className="mb-2">
                            <h1 className="title-24 title-20-mb color-black mb-2">{t("report.propertyReport")}</h1>
                            <p className="para-16 color-39393B">
                                {t('propertiesPage.YouHave')} <span className="bold-700 color-0F172A">{localStorage.getItem("LenghtListings")} </span> {t('propertiesPage.listingsL')}.
                            </p>
                        </div>
                    </div>
                    <div className="col-1 d-flex alig-items-center justify-content-end">
                        <GeneratedPdf change={isChange} onchangeMessageHandler={ChangeMessageHandler} />
                    </div>
                </div>

                <FilterReport property={reportValue} OnChangeProperty={ChangePropertyHandler} OnChangeColor={ChangeColorHandler} onchangeMessageHandler={ChangeMessageHandler} isChange={isChange} />

                <h1 className="title-24 title-20-mb color-black mb-3">{t('report.coverPhoto')}</h1>

                <CoverPhoto errors={errors} property={reportValue} OnChangeCover={ChangeCoverHandler} />

                <h1 className="title-24 title-20-mb color-black mb-3">{t('report.heroPhotos')}</h1>

                <HeroPhotos errors={errors} property={reportValue} images={images} OnChangeCover={ChangeHeroImageHandler} OnChangeHeroPhotos={ChangeHeroPhotosHandler}
                    onImagesAdded={(imageslist: any) => setReportValue(prev => ({
                        ...prev,
                        listImages: imageslist
                    }))} />

                <SectionPhotos reportValue={reportValue} OnChangeProperty={ChangePhotosHandler} />

                <h1 className="title-24 title-20-mb color-black mb-3">{t('report.propertySummary')}</h1>

                <PropertySummary errors={errors} property={reportValue} OnChangeImage={ChangeImageHandler} OnChangeDesc={ChangeDescHandler} />

                <PropertyDetails errors={errors} property={reportValue} OnChangePrice={handleChangePrice} OnChangeServiceCharge={handleChangeServiceCharge} OnChangeSize={handleChangeSize}
                    OnChange={handleChange} OnChangeOffering={HandleChangeOffering} />

                <FloorPlan errors={errors} property={reportValue} OnChangeFloorPlan={ChangeFloorPlanHandler} OnChangeNotes={ChangeNotesHandler} />

                <div className="row mb-3">

                    <div className="col-md-6">
                        <h1 className="title-24 title-20-mb color-black mb-3"> {t("generateDescTitle.location")}</h1>
                        <LocationReport errors={errors} property={reportValue} OnChangeLocation={ChangeLocationHandler} />
                    </div>

                    <div className="col-md-6">
                        <h1 className="title-24 title-20-mb color-black mb-3">{t("report.summary")}</h1>
                        <div className={`card border-16 mb-3 ${errors.addressDescription ? 'input-error' : ''}`}>
                            <div className="padd-20">
                                <TextAreaForm height="445px" ChangeTextarea={handleChangeDescLocation} value={reportValue?.location?.description} />
                            </div>
                        </div>
                    </div>

                </div>

                <AgentDetails agent={reportValue} OnChangeAgent={ChangeAgentHandler} />

                <div className="d-flex justify-content-end mb-4">
                    <ButtonGenerate OnGenerate={Generate} />
                </div>
            </div>

            <Snackbar open={openSnackbar} onClose={handleCloseSnackbar} autoHideDuration={10000}>
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={colorSnackbar || "info"}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {message}
                </Alert>
            </Snackbar>

            <Dialog
                fullWidth={true}
                maxWidth="lg"
                open={isOpenPdf}
                onClose={() => setIsOpenPdf(false)}
                aria-describedby="alert-dialog-slide-description">
                <DialogTitle id="customized-dialog-title" style={{ padding: "30px" }}>
                    <IconButton
                        aria-label="close"
                        className="mb-4"
                        onClick={() => setIsOpenPdf(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: 0, minHeight: '75vh', position: 'relative' }}>
                    {isPdfLoading && (
                        <div className="back-pdf-overlay">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="color-64748B para-16 mt-3">Preparing your PDF...</p>
                        </div>
                    )}
                    {pdfReportData && (
                        <PDFViewer
                            key={idPdf || 'pdf-viewer'}
                            width="100%"
                            height="auto"
                            style={{ border: 'none', minHeight: '75vh' }}
                            showToolbar={false}
                        >
                            <DocumentPdf propertyReport={pdfReportData} />
                        </PDFViewer>
                    )}

                </DialogContent>
                <DialogActions>
                    <div className="d-flex justify-content-end">
                        {pdfReportData && (
                            <PDFDownloadLink
                                document={<DocumentPdf propertyReport={pdfReportData} />}
                                fileName={`property-report-${idPdf}.pdf`}
                            >
                                {({ loading }) =>
                                    (!isPdfLoading && loading) ? (
                                        <button
                                            type="button"
                                            className="button-dash back-746AFF color-FFFFFF  border-746AFF padd-15-30"
                                            disabled
                                        >
                                            Downloading...
                                        </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="button-dash back-746AFF color-FFFFFF  border-746AFF padd-15-30"
                                        >
                                            Download PDF
                                        </button>
                                    )
                                }
                            </PDFDownloadLink>
                        )}
                    </div>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default CreatePropertyReport;